import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTabsStore, selectCurrentDbManagerTab } from '../store/tabsStore';
import { rememberDbFilterDraft, dbFilterDraftFor } from '../store/dbFilterDrafts';
import { DB_ENGINE_LABELS, DEFAULT_QUERY_MAX_ROWS } from '../extensions/dbEngines';
import {
  FIND_DEBOUNCE_MS, EMPTY_SORT_LEVELS, EMPTY_PENDING_ROWS, EMPTY_PENDING_EDITS,
  EMPTY_PENDING_DELETES,
} from './dbGridModel';
import { EmptyState } from './dbGridCells';
import { enumMetaByColumn } from './dbManager/dbEnumColumns';
import { DEFAULT_PAGE_SIZE, engineOf } from './dbManager/dbResultsTabHelpers';
import { dbResultsOverlays } from './dbManager/dbResultsOverlays';
import { dbToolbarActions } from './dbManager/dbToolbarActions';
import { useDbGridShortcuts } from './dbManager/useDbGridShortcuts';
import { useDbSelectionFind } from './dbManager/useDbSelectionFind';
import { useDbRowChanges } from './dbManager/useDbRowChanges';
import { useDbGridView } from './dbManager/useDbGridView';
import { useDbCellEditing } from './dbManager/useDbCellEditing';
import { useDbResultsActions } from './dbManager/useDbResultsActions';
import { useDbResultsQueries } from './dbManager/useDbResultsQueries';
import { renderDbResultsTab } from './dbManager/renderDbResultsTab';

export default function DbManagerResultsTab() {
  const tab = useTabsStore(selectCurrentDbManagerTab);
  // Armed by guardDirtyDbTab when navigation would discard staged grid edits.
  // This component is still mounted at that point: the guard aborts the
  // navigation before it happens, so the tab it belongs to is still active.
  const leaveGuard = useTabsStore((s) => s.dbTabLeaveGuard);
  const isQueryTab = !tab?.tableName;
  // Per tab, so two query tabs can hold different caps, and it rides the same
  // workspace tab snapshot pageSize already uses.
  const queryMaxRows = tab?.queryMaxRows ?? DEFAULT_QUERY_MAX_ROWS;
  // Read through a ref at invoke time: runFreeQuery is a useCallback with a
  // long dep list, and adding the cap to it would rebuild the callback (and
  // everything memoised on it) every time the picker moved.
  const queryMaxRowsRef = useRef(queryMaxRows);
  useEffect(() => {
    queryMaxRowsRef.current = queryMaxRows;
  }, [queryMaxRows]);

  // Elapsed time while a query runs. The duration was already measured and
  // written to history, so the number existed — it just was not shown until
  // after the fact, in a modal. A query with no visible clock and no endpoint
  // is indistinguishable from a hung one.
  const [runStartedAt, setRunStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [lastDurationMs, setLastDurationMs] = useState(null);

  useEffect(() => {
    if (runStartedAt == null) return undefined;
    // 100ms: fast enough to read as a clock, slow enough that it is not a
    // render every frame for the whole length of a slow query.
    const id = setInterval(() => setElapsedMs(Date.now() - runStartedAt), 100);
    return () => clearInterval(id);
  }, [runStartedAt]);
  const [filterDraft, setFilterDraftState] = useState(() => dbFilterDraftFor(tab));
  // Every write to the box is also remembered per tab, so a tab switch (or the
  // whole component unmounting) brings back what was typed — dbFilterDrafts.
  const setFilterDraft = useCallback((text) => {
    setFilterDraftState(text);
    const current = selectCurrentDbManagerTab(useTabsStore.getState());
    if (current?.tableName) rememberDbFilterDraft(current.id, text, current.filterText);
  }, []);
  const [sqlDraft, setSqlDraft] = useState(tab?.sql || '');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // The statement that produced `error`. Kept because the error string alone
  // is not diagnosable — "syntax error at or near \"FORM\"" means nothing
  // without the SQL — and nothing else in this tab retains it once the editor
  // moves on. Feeds the inline "Explain" affordance on the results grid.
  const [failedSql, setFailedSql] = useState(null);
  // Separate from `error` on purpose: `error` feeds ResultsGrid's hard
  // early-return (replaces the whole grid), which is right for a failed
  // fetch (there's no table to show) but wrong for a failed save/delete —
  // the grid still has valid rows the user needs to keep working with, so
  // that failure renders as its own section below the table instead.
  const [writeError, setWriteError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Mirrors of result/loading, so loadMoreRows can read the current values
  // without taking them as dependencies — it is called from a scroll handler
  // that fires many times a second, and re-creating it that often would
  // re-bind the listener with it.
  const resultRef = useRef(null);
  const loadingRef = useRef(false);
  const [pendingRowsByTab, setPendingRowsByTab] = useState({});
  const [pendingEditsByTab, setPendingEditsByTab] = useState({});
  // Rows marked for deletion, staged rather than deleted on the spot: a Set of
  // row indexes per tab, keyed the same way as the other two because "pending"
  // has always been per-tab here and a delete staged on one table must not
  // follow you to another.
  const [pendingDeletesByTab, setPendingDeletesByTab] = useState({});
  const [saving, setSaving] = useState(false);
  const [primaryKeyCols, setPrimaryKeyCols] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  // Custom date/datetime/time picker popover: { kind, target:'saved'|'pending',
  // id, col, value, rect } | null. Opened by the calendar affordance in a
  // temporal cell; anchored at the cell's rect.
  const [datePicker, setDatePicker] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  // Columns under a UNIQUE constraint/index — blanked when duplicating rows
  // (like the PK) so the staged copy can insert without a uniqueness clash.
  const [uniqueCols, setUniqueCols] = useState([]);
  // In-app replacement for window.confirm/alert (blocked by Tauri capability
  // config): { title, message, confirmText, type, hideCancel, onConfirm }.
  const [confirmRequest, setConfirmRequest] = useState(null);
  const [colSuggestions, setColSuggestions] = useState(null);
  // udtName carries the real type name when postgres reports the generic
  // "USER-DEFINED" (enums, domains, etc.) — prefer it so the header doesn't
  // just say "user-defined".
  const columnTypeMap = useMemo(() => {
    const map = {};
    for (const c of tableColumns) {
      map[c.name] = c.type === 'USER-DEFINED' && c.udtName ? c.udtName : c.type;
    }
    return map;
  }, [tableColumns]);
  // ENUM columns → their allowed values; their cells edit as a dropdown.
  const enumByColumn = useMemo(() => enumMetaByColumn(tableColumns), [tableColumns]);

  // Sort/page state lives in refs too so runTableQuery (stable, dep-free)
  // reads current values without re-creating on every change.
  // Sort levels in precedence order; [] is unsorted. Shift-clicking a header
  // adds a level, a plain click replaces them all.
  const [sortState, setSortState] = useState(EMPTY_SORT_LEVELS);
  const sortRef = useRef(EMPTY_SORT_LEVELS);
  // Rows arrive in chunks as you scroll, rather than a page at a time. What
  // used to be the page size is now the chunk size — how many rows each fetch
  // asks for.
  const chunkSizeRef = useRef(DEFAULT_PAGE_SIZE);
  // True once a chunk comes back short: there is nothing further to load, so
  // scrolling stops asking.
  const [allLoaded, setAllLoaded] = useState(false);
  const allLoadedRef = useRef(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [totalRows, setTotalRows] = useState(null);
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  // Spreadsheet-style cell selection, orthogonal to the row selection above:
  // a Set of cellKey() strings covering both saved and pending rows. Drives
  // Ctrl+C (copy as TSV) and Ctrl+V (fill every selected cell).
  const [selectedCells, setSelectedCells] = useState(() => new Set());
  // Where a Shift+click ranges FROM — see toggleRowSelected, which is the one
  // that maintains it.
  const selectionAnchorRef = useRef(null);
  const cellAnchorRef = useRef(null); // { kind, id, col } | null
  // A setter rather than bare `.current =` assignments: the anchor is written
  // from eight places, and routing them through one function keeps the option
  // of mirroring it into state (as the value panel briefly needed) from having
  // to be re-threaded through all of them again.
  const setCellAnchor = useCallback((next) => {
    cellAnchorRef.current = next;
  }, []);
  // DBeaver-style transposed/record view: Tab (with rows selected) flips the
  // grid so columns become rows and each selected row becomes its own
  // column — much easier to read wide tables a row or two at a time.
  const [transposed, setTransposed] = useState(false);

  // Multi-result-set state for query tabs: the full array from the _multi
  // command plus the switcher position; `result` always holds the selected
  // set so the grid/footer/copy paths below stay single-result.
  const [resultSets, setResultSets] = useState(null); // DbQueryResult[] | null
  const [resultSetIndex, setResultSetIndex] = useState(0);

  // Ctrl+F over the rows already on screen. Not a filter: it locates a value
  // inside the current page rather than narrowing what was fetched.
  const [findOpen, setFindOpen] = useState(false);
  const [findFocusNonce, setFindFocusNonce] = useState(0); // Ctrl+F again → re-select the bar's text
  /**
   * Two queries, because typing and searching have different deadlines.
   *
   * `findDraft` is what the input shows and must update on the keystroke.
   * `findQuery` is what actually gets searched, and searching is
   * rows × columns string comparisons over a result set that can be 10,000
   * rows — cheap once, but not once per keystroke while someone types at
   * speed, which is where the lag came from. Every match, highlight and
   * counter downstream reads `findQuery`, so they all settle together rather
   * than flickering through the prefixes of the word being typed.
   */
  const [findDraft, setFindDraft] = useState('');
  const [findQuery, setFindQuery] = useState('');
  const [findMatchCase, setFindMatchCase] = useState(false);
  const [findIndex, setFindIndex] = useState(0);
  useEffect(() => {
    if (findDraft === findQuery) return undefined;
    // Clearing is not a search — there is nothing to compute and nothing to
    // wait for, and a highlight that lingers after the box is empty reads as
    // the clear not having worked.
    if (findDraft === '') {
      setFindQuery('');
      return undefined;
    }
    const t = setTimeout(() => setFindQuery(findDraft), FIND_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [findDraft, findQuery]);

  const [contextMenu, setContextMenu] = useState(null); // { x, y, rowIndex, colIndex, column } | null
  const [cellViewer, setCellViewer] = useState(null); // { rowIndex, colIndex, value, readOnly } | null
  const [exportOpen, setExportOpen] = useState(false);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  // Row indexes to export, when the export was launched from a row selection
  // rather than from the whole table.
  // Row indexes the export offers as "Selected rows", captured when it opens.
  const [exportRows, setExportRows] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // A reconnect waiting on a password, plus the resolvers its prompt answers.
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  // The id of the query currently in flight, so it can be stopped. Mirrored in
  // a ref because runFreeQuery is dep-free and stable.
  const [runningQueryId, setRunningQueryId] = useState(null);
  const runningQueryIdRef = useRef(null);
  // Where this tab was scrolled to. A ref, not state: it changes on every
  // scroll event and nothing renders from it.
  const scrollTopRef = useRef(0);
  /**
   * What the grid should scroll to, and which tab that number belongs to —
   * ONE piece of state, deliberately.
   *
   * These were separate: the key was read straight off `tab?.id` while the
   * position was state written by the arrival effect. That desyncs for exactly
   * one render on every tab switch — the key is already the new tab while the
   * position is still the old tab's — and the grid, seeing a key it had not
   * handled, restored the stale number and recorded the new key, locking
   * itself out of the correction that arrived a render later. The symptom was
   * a tab switch landing at the top.
   *
   * Travelling together makes that state unrepresentable: the grid never sees
   * a new key without the position that goes with it.
   */
  /**
   * …and `anchor` says WHICH of the two restores this is.
   *
   * `true` only for a record-view flip, where coming back to the selected row
   * is the point and a pixel offset cannot survive the remount anyway. `false`
   * for a tab arrival, where the saved offset IS the answer — you left that tab
   * looking at a particular place, and a selection made earlier should not
   * yank you somewhere else on return.
   *
   * Decided HERE rather than in the grid, which cannot tell the two apart: a
   * flip and a tab switch both reach it as "restore something". The grid used
   * to anchor whenever anything was selected, which quietly changed tab
   * switches too.
   */
  const [scrollRestore, setScrollRestore] = useState({ key: null, top: 0, anchor: false });
  // True while the record view is showing a row selection that Tab PROMOTED
  // from a cell selection, so the trip back can undo it. A ref, not state:
  // nothing renders from it, and it must not re-trigger the flip.
  const promotedRowsRef = useRef(false);
  const sqlEditorApiRef = useRef(null);
  const pendingRowIdRef = useRef(0);
  const filterInputRef = useRef(null);

  const pendingRows = (tab && pendingRowsByTab[tab.id]) || EMPTY_PENDING_ROWS;
  const setPendingRows = useCallback(
    (updater) => {
      if (!tab) return;
      setPendingRowsByTab((prev) => {
        const current = prev[tab.id] || EMPTY_PENDING_ROWS;
        const next = typeof updater === 'function' ? updater(current) : updater;
        return { ...prev, [tab.id]: next };
      });
    },
    [tab]
  );

  const pendingEdits = (tab && pendingEditsByTab[tab.id]) || EMPTY_PENDING_EDITS;
  const setPendingEdits = useCallback(
    (updater) => {
      if (!tab) return;
      setPendingEditsByTab((prev) => {
        const current = prev[tab.id] || EMPTY_PENDING_EDITS;
        const next = typeof updater === 'function' ? updater(current) : updater;
        return { ...prev, [tab.id]: next };
      });
    },
    [tab]
  );

  const pendingDeletes = (tab && pendingDeletesByTab[tab.id]) || EMPTY_PENDING_DELETES;
  const setPendingDeletes = useCallback(
    (updater) => {
      if (!tab) return;
      setPendingDeletesByTab((prev) => {
        const current = prev[tab.id] || EMPTY_PENDING_DELETES;
        const next = typeof updater === 'function' ? updater(current) : updater;
        return { ...prev, [tab.id]: next };
      });
    },
    [tab]
  );

  const {
    handleQueryFailure, resultFilterRef, resetLoadMore, loadMoreRows, runTableQuery, fetchRowCount,
    runFreeQuery,
  } = useDbResultsQueries({
    tab, result, queryMaxRowsRef, setRunStartedAt, setElapsedMs, setLastDurationMs,
    setFilterDraftState, setSqlDraft, setResult, setError, setFailedSql, setWriteError, loading,
    setLoading, resultRef, loadingRef, setPendingRows, setPendingEditsByTab, setPendingEdits,
    setPendingDeletes, setPrimaryKeyCols, setEditingCell, setDatePicker, setTableColumns,
    setUniqueCols, setConfirmRequest, setColSuggestions, setSortState, sortRef, chunkSizeRef,
    setAllLoaded, allLoadedRef, setLoadingMore, loadingMoreRef, setTotalRows, setSelectedRows,
    setSelectedCells, selectionAnchorRef, setCellAnchor, setTransposed, setResultSets,
    setResultSetIndex, setContextMenu, setCellViewer, setExportOpen, setRunningQueryId,
    runningQueryIdRef, scrollTopRef, setScrollRestore, sqlEditorApiRef,
  });

  const {
    reconnecting, copiedFormat, copyResult, selectResultSet, exportFetchChunk, querySchema,
    reconnect,
  } = useDbResultsActions({
    tab, result, setResult, setError, setFailedSql, primaryKeyCols, tableColumns, sortRef,
    setSelectedRows, setSelectedCells, resultSets, setResultSetIndex, setPasswordPrompt,
  });

  const {
    addPendingRow, updatePendingCell, removePendingRow, typeIntoCell, typeIntoPendingCell,
    cancelEditCell, rememberScrollAcrossFlip, toggleRecordViewWhileEditing, commitEditCell,
    stageCellNull, stageCellValue, openDatePicker, applyDatePick, clearDatePick, openCellViewer,
    handleCellOpen,
  } = useDbCellEditing({
    result, isQueryTab, pendingRows, setPendingRows, pendingEdits, setPendingEdits, editingCell,
    setEditingCell, editingValue, setEditingValue, datePicker, setDatePicker, columnTypeMap,
    setSelectedRows, selectionAnchorRef, transposed, setTransposed, setCellViewer, scrollTopRef,
    setScrollRestore, pendingRowIdRef,
  });

  const {
    fkByColumn, navigateFk, columnWidths, transposedWidths, hiddenColumnNames, hiddenColumns,
    hiddenLive, drawnColIndexes, setHiddenColumns, formatDraft, rowsForAction, resultForRows,
    stopRunningQuery, hideColumn, handleColumnResize, handleTransposedResize, pendingEditCount,
    pendingDeleteCount, hasUnsavedGridChanges, rememberScrollTop, viewStateRef, refreshRows,
  } = useDbGridView({
    tab, result, isQueryTab, sqlDraft, setSqlDraft, loading, pendingRows, pendingEdits,
    pendingDeletes, runTableQuery, selectedRows, selectedCells, selectionAnchorRef, cellAnchorRef,
    transposed, runningQueryIdRef, scrollTopRef, fetchRowCount, resetLoadMore,
  });

  const {
    saveChanges, discardChanges, stageDeleteRows, unstageDeleteRows, toggleDeleteMark,
    deleteSelectedRows, deleteGestureCount, deleteGestureAllMarked, duplicateRow,
    duplicateSelectedRows, openContextMenu, openHeaderContextMenu, openBackgroundContextMenu,
    importDump,
  } = useDbRowChanges({
    tab, result, isQueryTab, runFreeQuery, setResult, setError, setFailedSql, setWriteError,
    loading, pendingRows, setPendingRows, pendingEdits, setPendingEdits, pendingDeletes,
    setPendingDeletes, saving, setSaving, primaryKeyCols, setEditingCell, tableColumns, uniqueCols,
    setConfirmRequest, runTableQuery, selectedRows, setSelectedRows, selectedCells,
    setSelectedCells, selectionAnchorRef, setCellAnchor, setContextMenu, setExportOpen,
    setExportRows, pendingRowIdRef, handleQueryFailure, fetchRowCount, hasUnsavedGridChanges,
  });

  const {
    toggleRowSelected, visualRows, find, findMatchKeys, activeFindKey, findPosRef, stepFind,
    closeFind, toggleCellSelected, applyCellWrites, gridCursorKey,
  } = useDbSelectionFind({
    tab, result, isQueryTab, error, loading, pendingRows, setPendingRows, pendingEdits,
    setPendingEdits, primaryKeyCols, editingCell, tableColumns, uniqueCols, sortState, allLoaded,
    totalRows, selectedRows, setSelectedRows, selectedCells, setSelectedCells, selectionAnchorRef,
    cellAnchorRef, setCellAnchor, transposed, findOpen, setFindOpen, findQuery, setFindDraft,
    setFindQuery, findMatchCase, findIndex, setFindIndex, contextMenu, setContextMenu, scrollTopRef,
    resultFilterRef, typeIntoCell, typeIntoPendingCell, handleCellOpen, drawnColIndexes,
    viewStateRef,
  });

  useDbGridShortcuts({
    tab, result, isQueryTab, sqlDraft, setError, pendingRows, pendingEdits, editingCell,
    selectedRows, setSelectedRows, selectedCells, setSelectedCells, selectionAnchorRef,
    setCellAnchor, transposed, setTransposed, findOpen, setFindOpen, setFindFocusNonce,
    setFindIndex, setHistoryOpen, promotedRowsRef, saveChanges, hasUnsavedGridChanges,
    rememberScrollAcrossFlip, drawnColIndexes, formatDraft, pendingEditCount, pendingDeleteCount,
    discardChanges, toggleDeleteMark, duplicateSelectedRows, visualRows, stepFind, findPosRef,
    closeFind, applyCellWrites,
  });

  if (!tab) {
    return (
      <div className="dbm-tab dbm-tab--empty">
        <EmptyState>No table tab selected</EmptyState>
      </div>
    );
  }

  if (tab.needsReconnect) {
    const engineLabel = DB_ENGINE_LABELS[engineOf(tab)] || 'the database';
    return (
      <div className="dbm-tab">
        <div className="dbm-reconnect">
          <div className="dbm-reconnect-title">{tab.tableName || tab.label}</div>
          <p className="dbm-reconnect-desc">
            This tab was restored from your last session, but the {engineLabel} connection
            closed when the app did. Reconnect to {tab.tableName ? 'load the table' : 'run queries'} again.
          </p>
          {error && <div className="dbm-reconnect-error">{error}</div>}
          <button type="button" className="dbm-run-btn" onClick={reconnect} disabled={reconnecting}>
            {reconnecting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </div>
    );
  }

  const {
    applyFilterFragment, commitFilter, commitSql, commitScript, handleOrderColumn,
    handleClearSortColumn, handleSortColumn, elapsedLabel, stopTitle, pageSize, maxRowsPicker,
    handlePageSizeChange,
  } = dbToolbarActions({
    tab, result, queryMaxRows, runFreeQuery, runStartedAt, elapsedMs, filterDraft, setFilterDraft,
    sqlDraft, loading, runTableQuery, setSortState, sortRef, chunkSizeRef, sqlEditorApiRef,
    fetchRowCount, resetLoadMore, hasUnsavedGridChanges,
  });

  const {
    overlays, handleFilterChange, insertColumnSuggestion, handleFilterKeyDown, findBarNode,
  } = dbResultsOverlays({
    tab, result, find, leaveGuard, isQueryTab, filterDraft, setFilterDraft, setSqlDraft, error,
    loading, pendingRows, pendingEdits, pendingDeletes, saving, primaryKeyCols, datePicker,
    setDatePicker, tableColumns, confirmRequest, setConfirmRequest, colSuggestions,
    setColSuggestions, columnTypeMap, enumByColumn, sortRef, totalRows, selectedRows, selectedCells,
    findOpen, findFocusNonce, findDraft, setFindDraft, findMatchCase, setFindMatchCase, findIndex,
    contextMenu, setContextMenu, cellViewer, setCellViewer, exportOpen, setExportOpen,
    columnPickerOpen, setColumnPickerOpen, exportRows, setExportRows, historyOpen, setHistoryOpen,
    passwordPrompt, filterInputRef, hasUnsavedGridChanges, commitFilter, exportFetchChunk,
    stageCellNull, stageCellValue, applyDatePick, clearDatePick, openCellViewer, fkByColumn,
    columnWidths, hiddenColumnNames, hiddenLive, setHiddenColumns, rowsForAction, resultForRows,
    hideColumn, handleColumnResize, pendingEditCount, refreshRows, discardChanges, stageDeleteRows,
    unstageDeleteRows, duplicateRow, importDump, stepFind, closeFind, applyFilterFragment,
    handleOrderColumn, handleClearSortColumn,
  });

  return renderDbResultsTab({
    tab, result, pageSize, isQueryTab, queryMaxRows, lastDurationMs, filterDraft, sqlDraft,
    setSqlDraft, error, failedSql, writeError, loading, loadMoreRows, pendingRows, pendingEdits,
    pendingDeletes, saving, primaryKeyCols, editingCell, editingValue, setEditingValue,
    tableColumns, colSuggestions, setColSuggestions, columnTypeMap, enumByColumn, sortState,
    allLoaded, loadingMore, totalRows, selectedRows, selectedCells, toggleRowSelected, transposed,
    resultSets, resultSetIndex, setExportOpen, setColumnPickerOpen, setExportRows, setHistoryOpen,
    runningQueryId, scrollRestore, sqlEditorApiRef, filterInputRef, copiedFormat, copyResult,
    selectResultSet, querySchema, addPendingRow, updatePendingCell, removePendingRow,
    cancelEditCell, toggleRecordViewWhileEditing, commitEditCell, stageCellNull, openDatePicker,
    handleCellOpen, fkByColumn, navigateFk, columnWidths, transposedWidths, hiddenColumns,
    hiddenLive, formatDraft, stopRunningQuery, handleColumnResize, handleTransposedResize,
    pendingEditCount, pendingDeleteCount, hasUnsavedGridChanges, rememberScrollTop, refreshRows,
    saveChanges, discardChanges, deleteSelectedRows, deleteGestureCount, deleteGestureAllMarked,
    openContextMenu, openHeaderContextMenu, openBackgroundContextMenu, findMatchKeys, activeFindKey,
    toggleCellSelected, gridCursorKey, commitFilter, commitSql, commitScript, handleSortColumn,
    elapsedLabel, stopTitle, maxRowsPicker, handlePageSizeChange, overlays, handleFilterChange,
    insertColumnSuggestion, handleFilterKeyDown, findBarNode,
  });
}

